import React, { useContext, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import {
  Eye,
  EyeOff,
  Mail,
  Lock,
  ArrowRight,
  ShieldCheck,
  CheckCircle2,
} from "lucide-react";
import { UserContext } from "./Context/UserContext";
import { API_URL } from "./Config/Env";
import Swal from "sweetalert2";

export default function Signin() {
  const navigate = useNavigate();
  const { loginUser } = useContext(UserContext);

  const [form, setForm] = useState({
    email: "",
    password: "",
  });

  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);

  const handleChange = (e) => {
    const { name, value } = e.target;

    setForm((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!form.email.trim() || !form.password) {
      Swal.fire({
        icon: "warning",
        title: "Missing Information",
        text: "Please enter your email address and password.",
        confirmButtonColor: "#2563EB",
      });

      return;
    }

    setLoading(true);

    try {
      const response = await fetch(`${API_URL}/signin/`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          email: form.email.trim(),
          password: form.password,
        }),
      });

      const data = await response.json();

      if (!response.ok) {
        Swal.fire({
          icon: "error",
          title: "Sign In Failed",
          text:
            data.message ||
            data.detail ||
            "The email or password you entered is incorrect.",
          confirmButtonColor: "#2563EB",
        });

        return;
      }

      const user = data.user;

      if (!user) {
        throw new Error("User information was not returned.");
      }

      /*
       * Save authentication details
       */
      localStorage.setItem("user_id", user.user_id || "");
      localStorage.setItem("email", user.email || "");
      localStorage.setItem("name", user.name || "");
      localStorage.setItem("phone", user.phone_number || "");

      if (user.profile_image) {
        localStorage.setItem(
          "profile_image",
          user.profile_image
        );
      } else {
        localStorage.removeItem("profile_image");
      }

      localStorage.setItem(
        "is_verified",
        String(user.is_verified ?? false)
      );

      localStorage.setItem("role", user.role || "");

      /*
       * Tokens
       */
      if (data.access_token) {
        localStorage.setItem(
          "access_token",
          data.access_token
        );

        // Keep this for parts of the application
        // that currently read localStorage.getItem("token")
        localStorage.setItem(
          "token",
          data.access_token
        );
      }

      if (data.refresh_token) {
        localStorage.setItem(
          "refresh_token",
          data.refresh_token
        );
      }

      /*
       * Global user object
       */
      const userData = {
        user_id: user.user_id,
        name: user.name,
        phone: user.phone_number,
        email: user.email,
        token: data.access_token,
        profile_image: user.profile_image,
        is_verified: user.is_verified,
        role: user.role,
      };

      localStorage.setItem(
        "user",
        JSON.stringify(userData)
      );

      // Update UserContext so the header changes immediately
      loginUser(userData);

      /*
       * Redirect based on role
       */
      if (
        user.role === "deliveryperson" &&
        user.is_verified === true
      ) {
        navigate("/delivery-orders");
      } else if (
        user.role === "admin" &&
        user.is_verified === true
      ) {
        navigate("/dashboard");
      } else {
        navigate("/");
      }
    } catch (error) {
      console.error("Sign-in error:", error);

      Swal.fire({
        icon: "error",
        title: "Unable to Sign In",
        text:
          "We couldn't connect to your account. Please check your connection and try again.",
        confirmButtonColor: "#2563EB",
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-slate-50">
      <div className="min-h-screen grid lg:grid-cols-2">
        {/* Left Brand Section */}
        <section className="relative hidden lg:flex bg-slate-950 text-white overflow-hidden">
          {/* Background glow */}
          <div className="absolute top-0 left-0 w-[500px] h-[500px] bg-blue-600/25 rounded-full blur-[150px]" />

          <div className="absolute -bottom-40 right-0 w-[500px] h-[500px] bg-cyan-400/10 rounded-full blur-[140px]" />

          {/* Background grid */}
          <div
            className="absolute inset-0 opacity-[0.04]"
            style={{
              backgroundImage: `
                linear-gradient(rgba(255,255,255,0.7) 1px, transparent 1px),
                linear-gradient(90deg, rgba(255,255,255,0.7) 1px, transparent 1px)
              `,
              backgroundSize: "50px 50px",
            }}
          />

          <div className="relative z-10 flex flex-col justify-between w-full p-12 xl:p-16">
            {/* Logo */}
            <Link
              to="/"
              className="inline-flex items-center gap-3 w-fit"
            >
              <img
                src="/logo.png"
                alt="Nexindi Tech"
                className="w-12 h-12 rounded-xl object-cover border border-white/20"
              />

              <div>
                <p className="font-bold text-xl tracking-tight">
                  NEXINDI
                </p>

                <p className="text-xs tracking-[0.25em] uppercase text-blue-400">
                  Tech
                </p>
              </div>
            </Link>

            {/* Main Content */}
            <div className="max-w-xl py-16">
              <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full border border-blue-400/20 bg-blue-500/10 text-blue-300 text-sm font-semibold mb-7">
                <span className="w-2 h-2 bg-blue-400 rounded-full" />

                Welcome Back
              </div>

              <h1 className="text-5xl xl:text-6xl font-bold tracking-tight leading-[1.08] mb-7">
                Technology built for
                <span className="block text-blue-400">
                  real-world impact.
                </span>
              </h1>

              <p className="text-lg text-slate-300 leading-relaxed mb-10 max-w-lg">
                Sign in to access your Nexindi account, manage
                services, track orders, interact with our platforms,
                and stay connected.
              </p>

              <div className="space-y-4">
                {[
                  "Secure account access",
                  "Manage your services and orders",
                  "Stay connected with Nexindi Tech",
                ].map((item, index) => (
                  <div
                    key={index}
                    className="flex items-center gap-3 text-slate-300"
                  >
                    <div className="w-8 h-8 rounded-lg bg-blue-500/10 border border-blue-400/20 flex items-center justify-center">
                      <CheckCircle2
                        size={16}
                        className="text-blue-400"
                      />
                    </div>

                    <span>{item}</span>
                  </div>
                ))}
              </div>
            </div>

            {/* Bottom */}
            <p className="text-sm text-slate-500">
              Empowering Africa through technology.
            </p>
          </div>
        </section>

        {/* Right Sign In Section */}
        <section className="relative flex items-center justify-center px-6 py-12 md:px-12 bg-white">
          {/* Mobile glow */}
          <div className="lg:hidden absolute top-0 left-0 w-[300px] h-[300px] bg-blue-100 rounded-full blur-[120px] opacity-60" />

          <div className="relative z-10 w-full max-w-md">
            {/* Mobile Logo */}
            <Link
              to="/"
              className="lg:hidden flex items-center justify-center gap-3 mb-12"
            >
              <img
                src="/logo.png"
                alt="Nexindi Tech"
                className="w-12 h-12 rounded-xl object-cover"
              />

              <div>
                <p className="font-bold text-xl text-slate-900 leading-tight">
                  NEXINDI
                </p>

                <p className="text-[10px] text-blue-600 uppercase tracking-[0.22em]">
                  Tech
                </p>
              </div>
            </Link>

            {/* Form Header */}
            <div className="mb-9">
              <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-blue-50 text-blue-600 text-xs font-semibold mb-5">
                <ShieldCheck size={14} />
                Secure Sign In
              </div>

              <h2 className="text-3xl md:text-4xl font-bold tracking-tight text-slate-900 mb-3">
                Welcome back
              </h2>

              <p className="text-slate-500 leading-relaxed">
                Enter your account details to continue to Nexindi
                Tech.
              </p>
            </div>

            {/* Form */}
            <form
              onSubmit={handleSubmit}
              className="space-y-5"
            >
              {/* Email */}
              <div>
                <label
                  htmlFor="email"
                  className="block text-sm font-semibold text-slate-700 mb-2"
                >
                  Email Address
                </label>

                <div className="relative">
                  <Mail
                    size={18}
                    className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-400"
                  />

                  <input
                    id="email"
                    type="email"
                    name="email"
                    required
                    autoComplete="email"
                    placeholder="you@example.com"
                    value={form.email}
                    onChange={handleChange}
                    className="w-full pl-12 pr-4 py-3.5 rounded-xl border border-slate-200 bg-white text-slate-900 outline-none transition-all duration-300 placeholder:text-slate-400 focus:border-blue-500 focus:ring-4 focus:ring-blue-500/10"
                  />
                </div>
              </div>

              {/* Password */}
              <div>
                <div className="flex items-center justify-between mb-2">
                  <label
                    htmlFor="password"
                    className="text-sm font-semibold text-slate-700"
                  >
                    Password
                  </label>

                  <Link
                    to="/forgot-password"
                    className="text-sm font-semibold text-blue-600 hover:text-blue-700 transition"
                  >
                    Forgot password?
                  </Link>
                </div>

                <div className="relative">
                  <Lock
                    size={18}
                    className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-400"
                  />

                  <input
                    id="password"
                    type={
                      showPassword ? "text" : "password"
                    }
                    name="password"
                    required
                    autoComplete="current-password"
                    placeholder="Enter your password"
                    value={form.password}
                    onChange={handleChange}
                    className="w-full pl-12 pr-12 py-3.5 rounded-xl border border-slate-200 bg-white text-slate-900 outline-none transition-all duration-300 placeholder:text-slate-400 focus:border-blue-500 focus:ring-4 focus:ring-blue-500/10"
                  />

                  <button
                    type="button"
                    onClick={() =>
                      setShowPassword(
                        (prev) => !prev
                      )
                    }
                    className="absolute right-4 top-1/2 -translate-y-1/2 text-slate-400 hover:text-blue-600 transition"
                    aria-label={
                      showPassword
                        ? "Hide password"
                        : "Show password"
                    }
                  >
                    {showPassword ? (
                      <EyeOff size={19} />
                    ) : (
                      <Eye size={19} />
                    )}
                  </button>
                </div>
              </div>

              {/* Submit */}
              <button
                type="submit"
                disabled={loading}
                className="group w-full flex items-center justify-center gap-3 bg-blue-600 text-white py-3.5 px-6 rounded-xl font-semibold hover:bg-blue-700 hover:-translate-y-0.5 hover:shadow-xl hover:shadow-blue-600/20 transition-all duration-300 disabled:opacity-60 disabled:cursor-not-allowed disabled:hover:translate-y-0 disabled:hover:shadow-none"
              >
                {loading ? (
                  <>
                    <span className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                    Signing In...
                  </>
                ) : (
                  <>
                    Sign In

                    <ArrowRight
                      size={18}
                      className="transition-transform group-hover:translate-x-1"
                    />
                  </>
                )}
              </button>
            </form>

            {/* Sign Up */}
            <div className="mt-8 pt-7 border-t border-slate-200 text-center">
              <p className="text-sm text-slate-500">
                Don't have a Nexindi account?{" "}
                <Link
                  to="/signup"
                  className="font-semibold text-blue-600 hover:text-blue-700"
                >
                  Create an account
                </Link>
              </p>
            </div>

            {/* Security note */}
            <div className="mt-7 flex items-start gap-3 rounded-2xl bg-slate-50 border border-slate-200 p-4">
              <ShieldCheck
                size={19}
                className="text-blue-600 flex-shrink-0 mt-0.5"
              />

              <p className="text-xs text-slate-500 leading-relaxed">
                Your account credentials are securely transmitted
                when you sign in. Never share your password with
                anyone.
              </p>
            </div>
          </div>
        </section>
      </div>
    </div>
  );
}